/**
 * RDB 3.23 spike, generalised in RDB 4.3 — add-to-cart for recipe syrup
 * ingredients on every recipe, not a hand-picked sample.
 *
 * Server side (sections/main-recipe.liquid) emits one JSON blob with the
 * recipe's recipe.syrups references, variants included and non-sellable bottles
 * already filtered out. This script matches the ingredient list items against
 * that list and injects a control after the ingredient text:
 *
 *   1. the product link the merchant already put in the ingredient line
 *      (`/products/<handle>`) — accepted only when the handle is one of the
 *      recipe's syrup references, so a link to a rum or to a bottle we no
 *      longer sell never becomes a cart control;
 *   2. otherwise a whole-word match of a sellable syrup title in the line text.
 *
 * It makes no request on page load. When the shopper adds something it POSTs
 * /cart/add.js and then refreshes the theme's own cart fragments
 * (?section_id=helper-cart), exactly like component-cart.js does, so the drawer
 * and the header count cannot disagree. The drawer is never opened.
 *
 * Without JavaScript nothing here runs and the page is unchanged.
 */
(function () {
  'use strict';

  var dataEl = document.querySelector('[data-recipe-cart-data]');
  if (!dataEl) return;

  var description = document.querySelector('.product__description');
  if (!description) return;

  var data = {};
  try {
    data = JSON.parse(dataEl.textContent) || {};
  } catch (error) {
    return;
  }
  var syrups = data.syrups || [];
  if (!syrups.length) return;

  /* Every word this control says is rendered by Liquid from locales/*.json and
     carried in the same blob (RDB 4.7), so a translated store never reads
     English out of the client. A missing key renders nothing rather than
     falling back to a hardcoded sentence — the blob and this file ship
     together, so a missing key is a build mistake, not a runtime state. */
  var strings = data.strings || {};

  /* Locale strings use Liquid's `{{ name }}` placeholders; substitute them
     here with values that are already plain text. */
  function format(template, values) {
    if (!template) return '';
    return String(template).replace(/\{\{\s*([a-z_]+)\s*\}\}/g, function (match, key) {
      return values && values[key] != null ? String(values[key]) : '';
    });
  }

  var byLength = syrups.slice().sort(function (a, b) {
    return (b.title || '').length - (a.title || '').length;
  });

  /* Handle -> syrup. This is the list of bottles this recipe is allowed to
     offer: whatever recipe.syrups references (minus anything not sellable,
     which the server already dropped), never a product found by guessing. */
  var byHandle = {};
  byLength.forEach(function (syrup) {
    if (syrup.handle) byHandle[String(syrup.handle).toLowerCase()] = syrup;
  });

  /* Wrappers the control may be placed inside: the element that owns the end of
     the ingredient line. Deliberately excludes A and STRONG, where the control
     would end up inside the merchant's link or in bold. P and DIV are included
     because Notion wraps whole ingredient lines in `<p class="p1">` — appending
     the control after that block gives it a line of its own (ma-cherie and 11
     more, RDB 4.9). */
  var INLINE_WRAPPERS = { SPAN: 1, P: 1, DIV: 1 };

  /* Content that keeps an otherwise empty element visible: a control must never
     be placed before an image, a video or another button. */
  var REPLACED_CONTENT = 'img,svg,picture,video,iframe,canvas,object,embed,input,select,textarea,button';

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function matchesName(text, name) {
    var pattern = '(^|[^\\p{L}\\p{N}])' + escapeRegExp(name.toLowerCase()) + '([^\\p{L}\\p{N}]|$)';
    try {
      return new RegExp(pattern, 'u').test(text);
    } catch (error) {
      return text.indexOf(name.toLowerCase()) !== -1;
    }
  }

  /* `/products/<handle>`, and the older `/collections/<c>/products/<handle>`
     form the merchant's Notion exports still carry in places. */
  function handleFromHref(href) {
    var match = /\/products\/([A-Za-z0-9][A-Za-z0-9_-]*)/.exec(href || '');
    return match ? match[1].toLowerCase() : '';
  }

  function listItems(list) {
    return Array.prototype.slice.call(list.querySelectorAll('li'));
  }

  /* How many distinct syrups this list can dress. Links win over names inside a
     line, because the merchant's own link is authorial intent; a whole-word
     name match is the fallback for the lines that were never linked. */
  function syrupFor(item, taken) {
    var links = item.querySelectorAll('a[href*="/products/"]');
    var i;
    for (i = 0; i < links.length; i += 1) {
      var handle = handleFromHref(links[i].getAttribute('href'));
      if (handle && byHandle[handle] && !taken[handle]) {
        return { syrup: byHandle[handle], via: 'link' };
      }
    }
    var text = (item.textContent || '').toLowerCase();
    for (i = 0; i < byLength.length; i += 1) {
      var syrup = byLength[i];
      var key = String(syrup.handle).toLowerCase();
      if (taken[key]) continue;
      if (!matchesName(text, syrup.title)) continue;
      return { syrup: syrup, via: 'name' };
    }
    return null;
  }

  function scoreList(list) {
    var taken = {};
    var found = 0;
    listItems(list).forEach(function (item) {
      var match = syrupFor(item, taken);
      if (!match) return;
      taken[String(match.syrup.handle).toLowerCase()] = true;
      found += 1;
    });
    return found;
  }

  /* Pick the list that actually holds the ingredients: the one that dresses the
     most syrups (earliest wins ties). Descriptions are Notion exports and some
     start with an empty <ul>, so "first list" is not good enough. */
  var lists = Array.prototype.slice.call(description.querySelectorAll('ul, ol')).filter(function (list) {
    return list.querySelector('li');
  });

  var ingredientList = null;
  var bestMatches = 0;

  lists.forEach(function (list) {
    var count = scoreList(list);
    if (count > bestMatches) {
      bestMatches = count;
      ingredientList = list;
    }
  });

  if (!ingredientList || !bestMatches) return;

  var cartIcon =
    '<svg class="recipe-cart__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
    '<path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" ' +
    'd="M1.5 2.5h2l2.2 10.1h9.4l1.9-7.1H5.1"/>' +
    '<circle cx="7.4" cy="16.2" r="1.4" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
    '<circle cx="14.6" cy="16.2" r="1.4" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
    '</svg>';

  function labelFor(syrup, variant) {
    return format(strings.add_label, { syrup: syrup.title, variant: variant.title });
  }

  /* Mirror component-cart.js: fetch the cart section, swap the theme's own
     #AjaxCartForm / #AjaxCartSubtotal, and read the counts from that fresh
     markup. Never increment a local counter — the sidebar is server-rendered at
     page load, so a +1 leaves "SHOPPING CART (1)" above "Your cart is currently
     empty". The .cart-form MutationObserver in layout/theme.liquid then clears
     cart-is-empty. We deliberately do NOT call .show() on #site-cart-sidebar. */
  function refreshCartUi() {
    return fetch('?section_id=helper-cart', { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart section unavailable');
        return response.text();
      })
      .then(function (text) {
        var fresh = new DOMParser().parseFromString(text, 'text/html');
        var freshForm = fresh.getElementById('AjaxCartForm');
        var freshSubtotal = fresh.getElementById('AjaxCartSubtotal');
        var form = document.getElementById('AjaxCartForm');
        var subtotal = document.getElementById('AjaxCartSubtotal');
        if (!freshForm || !freshSubtotal || !form || !subtotal) return;

        form.innerHTML = freshForm.innerHTML;
        if (typeof form.ajaxifyCartItems === 'function') form.ajaxifyCartItems();
        subtotal.innerHTML = freshSubtotal.innerHTML;

        var countNode = form.querySelector('[data-cart-count]');
        var totalNode = form.querySelector('[data-cart-total]');
        var count = countNode ? (countNode.textContent || '').trim() : '';

        if (count !== '') {
          Array.prototype.forEach.call(document.querySelectorAll('[data-header-cart-count]'), function (node) {
            node.textContent = count;
          });
          var sidebar = document.getElementById('site-cart-sidebar');
          if (sidebar) sidebar.setAttribute('data-cart-items', count);
        }
        if (totalNode) {
          var total = (totalNode.textContent || '').trim();
          Array.prototype.forEach.call(document.querySelectorAll('[data-header-cart-total]'), function (node) {
            node.textContent = total;
          });
        }

        form.dispatchEvent(new Event('cart-updated'));
      })
      .catch(function () {
        /* The item is already in the cart; a failed refresh must not report a
           failed add. The next page view renders the true cart. */
      });
  }

  function buildControl(syrup, item, via) {
    var variants = syrup.variants || [];
    if (!variants.length) return;

    var firstAvailable = null;
    variants.forEach(function (variant) {
      if (!firstAvailable && variant.available) firstAvailable = variant;
    });
    var selected = firstAvailable || variants[0];

    var wrap = document.createElement('span');
    wrap.className = 'recipe-cart';
    /* QA hook: which bottle this control offers and how it was matched
       ("link" = the merchant linked the syrup in the ingredient line,
       "name" = whole-word fallback against a sellable syrup title). */
    wrap.setAttribute('data-recipe-cart-syrup', syrup.handle);
    wrap.setAttribute('data-recipe-cart-match', via || 'name');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'recipe-cart__btn';
    button.innerHTML = cartIcon;
    var buttonLabel = document.createElement('span');
    buttonLabel.className = 'recipe-cart__label';
    buttonLabel.textContent = strings.add_short || '';
    button.appendChild(buttonLabel);
    button.setAttribute('aria-label', labelFor(syrup, selected));
    if (!firstAvailable) {
      button.disabled = true;
      button.setAttribute('aria-label', format(strings.add_sold_out_label, { syrup: syrup.title }));
    }

    var status = document.createElement('span');
    status.className = 'recipe-cart__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    wrap.appendChild(button);
    wrap.appendChild(status);

    var popover = null;
    var timer = null;

    function clearStatus() {
      status.textContent = '';
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function say(message, sticky) {
      clearStatus();
      status.textContent = message;
      if (!sticky) {
        timer = window.setTimeout(function () {
          status.textContent = '';
          timer = null;
        }, 4000);
      }
    }

    function setBusy(busy) {
      button.disabled = busy;
      if (popover) {
        var addButton = popover.querySelector('.recipe-cart__add');
        if (addButton) addButton.disabled = busy;
      }
    }

    function closePopover(returnFocus) {
      if (!popover) return;
      popover.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      if (returnFocus) button.focus();
    }

    function add(variant) {
      if (!variant) return;
      setBusy(true);
      say(strings.adding, true);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      })
        .then(function (response) {
          return response.json().then(function (body) {
            return { ok: response.ok, body: body };
          });
        })
        .then(function (result) {
          if (!result.ok) throw new Error('add failed');
          closePopover(true);
          /* Short confirmation on purpose (RDB 3.24): the status sits beside a
             26px pill inside the ingredient line, and "Added: <syrup> <size>"
             wrapped to a second line on a phone, which made that one row taller
             than its neighbours. The button's aria-label already names the
             syrup and size, and the cart fragment below shows the line item. */
          say(strings.added);
          refreshCartUi();
        })
        .catch(function () {
          say(strings.error);
        })
        .then(function () {
          setBusy(false);
        });
    }

    function openPopover() {
      if (!popover) return;
      popover.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      popover.style.left = '0';
      popover.style.right = 'auto';
      popover.style.top = 'calc(100% + 0.375rem)';
      popover.style.bottom = 'auto';
      var rect = popover.getBoundingClientRect();
      if (rect.right > window.innerWidth - 8) {
        popover.style.left = 'auto';
        popover.style.right = '0';
        rect = popover.getBoundingClientRect();
      }
      /* Flip above the icon when the panel would open past the bottom of the
         viewport, then nudge it into view as a last resort. */
      if (rect.bottom > window.innerHeight - 8) {
        var buttonRect = button.getBoundingClientRect();
        if (buttonRect.top > rect.height + 16) {
          popover.style.top = 'auto';
          popover.style.bottom = 'calc(100% + 0.375rem)';
          rect = popover.getBoundingClientRect();
        }
        if (rect.bottom > window.innerHeight - 8 || rect.top < 8) {
          popover.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
      var checked = popover.querySelector('input:checked') || popover.querySelector('input:not([disabled])');
      if (checked) checked.focus();
    }

    if (variants.length > 1) {
      popover = document.createElement('div');
      popover.className = 'recipe-cart__popover';
      popover.setAttribute('role', 'group');
      popover.setAttribute('aria-label', format(strings.choose_size_for, { syrup: syrup.title }));
      popover.hidden = true;

      var groupName = 'recipe-cart-' + syrup.handle;

      variants.forEach(function (variant) {
        var option = document.createElement('label');
        option.className = 'recipe-cart__option' + (variant.available ? '' : ' recipe-cart__option--soldout');

        var input = document.createElement('input');
        input.type = 'radio';
        input.name = groupName;
        input.value = String(variant.id);
        input.checked = variant.id === selected.id;
        input.disabled = !variant.available;
        if (variant.available) {
          input.addEventListener('change', function () {
            selected = variant;
            button.setAttribute('aria-label', labelFor(syrup, variant));
          });
        }

        var title = document.createElement('span');
        title.textContent = variant.title;

        var price = document.createElement('span');
        price.className = 'recipe-cart__option-price';
        price.textContent = variant.price;

        option.appendChild(input);
        option.appendChild(title);
        option.appendChild(price);
        if (!variant.available) {
          var soldOut = document.createElement('span');
          /* The dash is punctuation, the words are the store's translation. */
          soldOut.textContent = '— ' + (strings.sold_out || '');
          option.appendChild(soldOut);
        }
        popover.appendChild(option);
      });

      var addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'recipe-cart__add';
      addButton.textContent = strings.add_to_cart || '';
      addButton.addEventListener('click', function () {
        add(selected);
      });
      popover.appendChild(addButton);

      popover.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.preventDefault();
          event.stopPropagation();
          closePopover(true);
        }
      });

      wrap.appendChild(popover);

      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-haspopup', 'true');

      button.addEventListener('click', function (event) {
        event.preventDefault();
        if (popover.hidden) {
          openPopover();
        } else {
          closePopover(false);
        }
      });

      document.addEventListener('click', function (event) {
        if (popover.hidden) return;
        if (wrap.contains(event.target)) return;
        closePopover(false);
      });

      document.addEventListener('keydown', function (event) {
        if (popover.hidden) return;
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        closePopover(true);
      });
    } else {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        add(selected);
      });
    }

    /* The control has to ride inside the ingredient line, which means it has to
       be inserted before — not after — any trailing line break. Merchant
       descriptions are Notion exports and a good number of ingredient <li>s end
       with a stray `<br>` (zadaran-storm: `…</strong></a><br>`; hurricane,
       tokyo-dry and ~50 more). `appendChild` puts the pill behind that break,
       where it occupies a line of its own and the row grows from 30.8px to
       59.59px at 360 (RDB 4.6 finding 1). Inserting before the trailing run of
       breaks and whitespace leaves the merchant's markup untouched and keeps
       the pill inline. */
    var baseline = item.getBoundingClientRect().height;
    insertInline(item, wrap);
    fitRow(item, wrap, baseline);
  }

  function insertInline(item, node) {
    var host = item;
    for (var depth = 0; depth < 16; depth += 1) {
      var last = lastRealChild(host);
      /* Descend into the element that owns the end of the line. Two shapes need
         it: the trailing <br> sitting one level down (`…<br></span>` in
         cure-all-toddy, `…<br></p>` in cesars-rum-punch, RDB 4.7), and the
         Notion <p class="p1"> that wraps a whole ingredient line — appending
         after that block is what put the pill on its own line in RDB 4.9. */
      if (last && last.nodeType === 1 && INLINE_WRAPPERS[last.tagName]) {
        host = last;
        continue;
      }
      liftTrailingBreak(host);
      var run = trailingRun(host);
      if (run.ref) {
        host.insertBefore(node, run.ref);
      } else {
        host.appendChild(node);
      }
      return;
    }
  }

  /* The last <br> of the line, however deeply it is nested, provided nothing
     but whitespace and empty inline elements follows it. */
  function deepestTrailingBreak(el) {
    var node = el;
    for (var depth = 0; depth < 16; depth += 1) {
      var last = lastRealChild(node);
      if (!last) return null;
      if (last.nodeType === 1 && last.tagName === 'BR') return last;
      if (last.nodeType !== 1) return null;
      node = last;
    }
    return null;
  }

  /* Some Notion lines close with the <br> INSIDE the bold/link run —
     `<strong><a>Rio Red Grapefruit Cordial</a><br></strong>` (ipa-mazing) and
     `<strong><a>Real Grenadine<br></a></strong>` (pomegranate-mojito). The
     control cannot go in there (it would sit inside the merchant's link, in
     bold), and anywhere after it lands behind the break. So the break is lifted
     out to the line owner one level at a time: it still ends the same line, the
     bold text is untouched, and the control goes in front of it. */
  function liftTrailingBreak(host) {
    var br = deepestTrailingBreak(host);
    if (!br || br.parentNode === host) return;
    var wrapper = br.parentNode;
    while (wrapper && wrapper !== host && wrapper.parentNode) {
      var parent = wrapper.parentNode;
      if (lastRealChild(parent) !== wrapper) break;
      parent.insertBefore(br, wrapper.nextSibling);
      wrapper = parent;
    }
  }

  /* An element with no text and no replaced content — `<strong></strong>` or a
     bare `<span></span>` left at the end of a Notion line. It renders nothing,
     but it sits between the control and the line, so the control has to be
     inserted before it, not after (daiq-in-black: `…</a><br><strong></strong>`,
     which put the pill behind the break and grew the row by 30.79px). */
  function isEmptyInline(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.tagName === 'BR') return false;
    if (/\S/.test(node.textContent || '')) return false;
    return !(node.querySelector && node.querySelector(REPLACED_CONTENT));
  }

  /* Trailing run of <br>s, whitespace and empty inline elements: the first node
     of the run, and whether the run contains an actual line break. */
  function trailingRun(el) {
    var ref = null;
    var hasBreak = false;
    var child = el.lastChild;
    while (child) {
      var isBreak = child.nodeType === 1 && child.tagName === 'BR';
      var isSpace = child.nodeType === 3 && !/\S/.test(child.nodeValue || '');
      if (!isBreak && !isSpace && !isEmptyInline(child)) break;
      if (isBreak) hasBreak = true;
      ref = child;
      child = child.previousSibling;
    }
    return { ref: ref, hasBreak: hasBreak };
  }

  function lastRealChild(el) {
    var child = el.lastChild;
    while (child) {
      if (child.nodeType === 3 && !/\S/.test(child.nodeValue || '')) {
        child = child.previousSibling;
        continue;
      }
      if (isEmptyInline(child)) {
        child = child.previousSibling;
        continue;
      }
      break;
    }
    return child;
  }

  /* RDB 4.9 — the row-growth safety net. The card asks for the icon-only
     fallback to be chosen by measurement, not by guessing from the text length:
     place the full pill, measure the row, and only when the row still grew
     (> 3px) swap that row's control to the icon-only variant and measure again.
     `data-recipe-cart-compact` / `data-recipe-cart-overflow` are QA hooks: the
     sweep over the whole library reads them instead of re-deriving the state. */
  var MAX_ROW_GROWTH = 3;

  function rowHeight(item) {
    return item.getBoundingClientRect().height;
  }

  function fitRow(item, wrap, baseline) {
    var grown = rowHeight(item) - baseline;
    if (grown <= MAX_ROW_GROWTH) {
      wrap.classList.remove('recipe-cart--compact');
      wrap.removeAttribute('data-recipe-cart-compact');
      wrap.removeAttribute('data-recipe-cart-overflow');
      return 'full';
    }
    wrap.classList.add('recipe-cart--compact');
    wrap.setAttribute('data-recipe-cart-compact', 'true');
    grown = rowHeight(item) - baseline;
    if (grown > MAX_ROW_GROWTH) {
      wrap.setAttribute('data-recipe-cart-overflow', grown.toFixed(2));
      return 'overflow';
    }
    wrap.removeAttribute('data-recipe-cart-overflow');
    return 'compact';
  }

  /* The breakpoint that hides the label is 480px and the row's own wrapping
     depends on the width, so the fit has to be re-decided when the viewport
     changes or the webfont lands (font metrics move the line). */
  function refitAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.recipe-cart'), function (wrap) {
      var popover = wrap.querySelector('.recipe-cart__popover');
      if (popover && !popover.hidden) return;
      var item = wrap.closest ? wrap.closest('li') : null;
      if (!item) return;
      var display = wrap.style.display;
      wrap.style.display = 'none';
      var baseline = rowHeight(item);
      wrap.style.display = display;
      wrap.classList.remove('recipe-cart--compact');
      wrap.removeAttribute('data-recipe-cart-compact');
      wrap.removeAttribute('data-recipe-cart-overflow');
      fitRow(item, wrap, baseline);
    });
  }

  var refitTimer = null;
  window.addEventListener('resize', function () {
    if (refitTimer) window.clearTimeout(refitTimer);
    refitTimer = window.setTimeout(refitAll, 150);
  });
  window.addEventListener('load', refitAll);
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(refitAll);
  }

  var taken = {};
  listItems(ingredientList).forEach(function (item) {
    var match = syrupFor(item, taken);
    if (!match) return;
    taken[String(match.syrup.handle).toLowerCase()] = true;
    buildControl(match.syrup, item, match.via);
  });
})();
